// https://fluentsite.z22.web.core.windows.net/quick-start
import {
  Button,
  FluentProvider,
  Text,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  tokens,
} from "@fluentui/react-components";
import { Component, ErrorInfo, lazy, ReactNode, Suspense, useEffect } from "react";
import { HashRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { app } from "@microsoft/teams-js";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { Spinner } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { TeamsFxContext } from "./Context";
import config from "./../lib/config";

const Admin = lazy(() => import("./Admin"));
const Requests = lazy(() => import("./Requests"));
const Home = lazy(() => import("./Home"));
const ChannelTabInformations = lazy(() => import("./ChannelTabInformations"));
const Analytics = lazy(() => import("./Analytics/Analytics"));

const chunkReloadKey = "teams-governor-chunk-reload";

function reloadWithCacheBust() {
  const url = new URL(window.location.href);
  url.searchParams.set("chunk-reload", Date.now().toString());
  window.location.replace(url.toString());
}

class ChunkLoadRecoveryBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunkLoadError = error.name === "ChunkLoadError" || /Loading chunk \d+ failed/i.test(error.message);

    if (isChunkLoadError && !sessionStorage.getItem(chunkReloadKey)) {
      sessionStorage.setItem(chunkReloadKey, "true");
      reloadWithCacheBust();
      return;
    }

    console.error("[App] Route loading failed", error, errorInfo);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export default function App() {
  const { t } = useTranslation("common");
  const { loading, theme, themeString, teamsUserCredential } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint!,
    clientId: config.clientId!,
  });
  useEffect(() => {
    console.log("[App][auth] credential-state", {
      loading,
      hasCredential: Boolean(teamsUserCredential),
      url: typeof window === "undefined" ? undefined : window.location.href,
    });
  }, [loading, teamsUserCredential]);
  useEffect(() => {
    loading &&
      app.initialize().then(() => {
        // Hide the loading indicator.
        app.notifySuccess();
      });
  }, [loading]);
  useEffect(() => {
    const timeout = window.setTimeout(() => sessionStorage.removeItem(chunkReloadKey), 10000);
    return () => window.clearTimeout(timeout);
  }, []);
  return (
    <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
      <FluentProvider
        theme={
          themeString === "dark"
            ? teamsDarkTheme
            : themeString === "contrast"
            ? teamsHighContrastTheme
            : teamsLightTheme
        }
        style={{ background: tokens.colorTransparentBackground }}
      >
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>        
          {!loading && (
            <ChunkLoadRecoveryBoundary
              fallback={
                <div role="alert">
                  <Text block>{t("Common.UpdateRequired", { defaultValue: "Une mise à jour de l'application est disponible." })}</Text>
                  <Button appearance="primary" onClick={reloadWithCacheBust}>
                    {t("Common.Reload", { defaultValue: "Recharger" })}
                  </Button>
                </div>
              }
            >
              <Suspense fallback={<Spinner label={t("Administration.Loading")} />}>
                <Routes>
                  <Route path="/home" element={<Home />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/requests" element={<Requests />} />
                  <Route path="/informations" element={<ChannelTabInformations />} />
                  <Route path="*" element={<Navigate to={"/informations"} />}></Route>
                </Routes>
              </Suspense>
            </ChunkLoadRecoveryBoundary>
          )}
        </Router>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
}
